import { FC, useCallback, useState, useMemo, useEffect, useRef, type RefObject } from 'react'
import { useChat } from '../../contexts/ChatContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { ChatUI } from '../chat/ChatUI'
import { Sidebar } from '../sidebar/Sidebar'
import { MobileSidebarDrawer } from '../sidebar/MobileSidebarDrawer'
import { WorkspaceManagerDialog } from '../sidebar/WorkspaceManagerDialog'
import { PatientDataInputDialog, PatientDataSaveError } from '../chat/PatientDataInputDialog'
import { useSidebar } from '../../hooks/useSidebar'
import { ProjectDetailView } from '../project/ProjectDetailView'
import { SidebarRail } from '../sidebar/SidebarRail'
import { PatientPanel, fromExtractions } from '../patient-panel'
import { IntakePage } from '../IntakePage'
import { IntakeProcessor } from '../IntakeProcessor'
import { FlowProcessor } from '../FlowProcessor'
import { CasePanelChat } from '../chat/case-panel/CasePanelChat'
import { ThinkingSidePanel } from '../chat/ThinkingSidePanel'
import { CanvasPanel } from '../canvas/CanvasPanel'
import { MobileWorkspaceShell, type MobileWorkspaceShellHandle } from './MobileWorkspaceShell'
import { useCanvas, type CanvasTab } from '../../hooks/useCanvas'
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable'
import type { ImperativePanelHandle } from 'react-resizable-panels'
import { Upload, Stethoscope, FileText, AlertTriangle } from 'lucide-react'
import { failedFileNames, uploadFilesInOrder } from '../../services/uploadFilesInOrder'
import {
  startPipeline,
  startFlowPipeline,
  subscribe as subscribePipeline,
  getSnapshot as getPipelineSnapshot,
  clearPipeline,
  clearFlowPipeline,
  PIPELINE_AGENT_IDS,
} from '../../services/pipelineManager'
import { ConsentModal, EncounterRecorder, NoteWithSources } from '../encounter'
import { useEncounter } from '../../contexts/EncounterContext'
// cn import removed — no longer used after handle simplification
import type { WorkspaceType } from '../../services/workspaceService'
import { workspaceService } from '../../services/workspaceService'
import type { FlowWorkspaceResults } from '../../hooks/useFlowPipeline'
import { WorkspaceLoadingSkeleton } from './WorkspaceLoadingSkeleton'

const LAYOUT_STORAGE_KEY = 'caseysPanel.panelLayout'

interface ExpandedThinking {
  content: string
  durationMs?: number
  isStreaming?: boolean
}

interface WorkspaceManagerProps {
  userFirstName?: string
  userFullName?: string
  onOpenSettings?: () => void
  onHeaderChange?: (info: { displayName: string | null, phase: string | null, type: string | null }) => void
  onAddNotification?: (type: 'agent_activity' | 'clinical_alert' | 'content_change', message: string) => void
  onFilesChange?: (info: {
    uploadedFiles: Array<{ id: string; name: string; type: string; timestamp: string }>
    artifacts: Array<{ id: string; title: string; type: string; timestamp: string; artifactId: string }>
    workspaceId: string
  }) => void
  canvasTabRef?: React.MutableRefObject<((tab: string) => void) | null>
  onRegisterMobileMenu?: (handler: (() => void) | null) => void
  onLoadNotifications?: (notifications: any[], workspaceId: string) => void
}

/**
 * Workspace manager -- phase-based routing for the 3-phase clinical workflow.
 *
 * Phase routing:
 *   - null / 'intake'     -> IntakePage (file upload + workspace type selector)
 *   - 'processing'        -> IntakeProcessor (admission) or FlowProcessor (flow)
 *   - 'workspace'         -> Main workspace (canvas + chat + patient panel)
 *
 * Phase transitions:
 *   1. Intake -> Processing: user uploads files, workspace created, pipeline starts
 *   2. Processing -> Workspace: pipeline completes (or user enters early)
 *   3. Flow -> Admission: user clicks "Convert to Admission" in flow workspace
 */
export const WorkspaceManager: FC<WorkspaceManagerProps> = ({ userFirstName, userFullName, onOpenSettings, onHeaderChange, onAddNotification, onFilesChange, canvasTabRef, onRegisterMobileMenu, onLoadNotifications }) => {
  const {
    createNewChat,
    currentSessionId,
    sessions,
    viewMode,
    activeProjectId,
    closeProject,
    patientData,
    patientPanelMode,
    togglePatientPanelMode,
    showPatientDataInput,
    setShowPatientDataInput,
    parseAndSavePatientText,
    parseAndSavePatientTextForWorkspace,
    clearPatientData,
    reconcilePatientData,
    isReconciling,
    files,
    agentSessions,
    activeAgentId,
    loadAgentSessions,
    clearAgentSessions,
    setPatientDataDirect,
    setActiveWorkspaceId: setChatWorkspaceId,
    loadSessionForWorkspace,
    setNotificationCallback,
    setRefreshClinicalDataCallback,
    setRefreshArtifactCallback,
    setCanvasUpdateCallback,
  } = useChat()

  const {
    activeWorkspaceId,
    createWorkspace,
    saveState,
    hydrationData,
    rehydrate,
    refreshClinicalData,
    refreshArtifact,
    pendingWorkspaceType,
    isHydrating,
  } = useWorkspace()

  const {
    showSidebar,
    toggleSidebar,
    isMobile,
  } = useSidebar()

  const {
    status: encounterStatus,
    resetEncounter,
    generatedNoteContent,
    transcript,
    error: encounterError,
    warnings: encounterWarnings,
  } = useEncounter()

  // Extraction progress derived from file parsing status
  const deepExtractingFile = files.find(f => f.parsingStatus === 'quick_done')
  const extractionPhase = deepExtractingFile?.extractionPhase

  const getExtractionMessage = () => {
    switch (extractionPhase) {
      case 'extraction': return 'Extracting clinical data...'
      case 'reconciliation': return 'Reconciling extractions...'
      case 'safety_check': return 'Running safety checks...'
      default: return 'Analyzing clinical details...'
    }
  }

  // Canvas for admission/flow workspaces
  const canvas = useCanvas({ workspaceId: activeWorkspaceId })
  const { loadFromArtifacts, clearCanvases } = canvas

  // Dismiss desktop sidebar when clicking the content overlay
  const dismissSidebar = useCallback(() => {
    if (!isMobile && showSidebar) toggleSidebar()
  }, [isMobile, showSidebar, toggleSidebar])

  // Close the mobile drawer after a sidebar navigation (switch/create) so a single
  // tap both navigates and dismisses. No-op on desktop, where the sidebar is inline.
  const closeMobileSidebar = useCallback(() => {
    if (isMobile && showSidebar) toggleSidebar()
  }, [isMobile, showSidebar, toggleSidebar])

  // Resizable panel refs for imperative collapse/expand
  const patientPanelRef = useRef<ImperativePanelHandle>(null)
  const canvasPanelRef = useRef<ImperativePanelHandle>(null)
  const mobileShellRef = useRef<MobileWorkspaceShellHandle>(null)
  const [isPatientPanelCollapsed, setIsPatientPanelCollapsed] = useState(false)
  const [isCanvasPanelCollapsed, setIsCanvasPanelCollapsed] = useState(false)

  // Expanded reasoning side panel — mirrors ChatUI's ThinkingSidePanel wiring.
  const [expandedThinking, setExpandedThinking] = useState<ExpandedThinking | null>(null)
  const handleExpandThinking = useCallback((content: string, durationMs?: number) => {
    setExpandedThinking({ content, durationMs, isStreaming: false })
  }, [])
  const handleCloseThinking = useCallback(() => {
    setExpandedThinking(null)
  }, [])

  // Per-turn latency/token metadata for the canvas footer (Unit 3).
  const lastTurnMeta = agentSessions[activeAgentId]?.lastTurnMeta

  // Persist panel layout sizes to localStorage
  const handlePanelLayout = useCallback((sizes: number[]) => {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(sizes))
  }, [])

  // Open a canvas tab from an inline-canvas card. Expand the rail first if it is
  // collapsed (default desktop state after dismissal) — otherwise setActiveTab is a no-op.
  const handleOpenCanvas = useCallback((tab: string) => {
    if (isCanvasPanelCollapsed) canvasPanelRef.current?.expand()
    canvas.setActiveTab(tab as CanvasTab)
  }, [isCanvasPanelCollapsed, canvas])

  const getSavedLayout = useCallback((panelCount: number): number[] | undefined => {
    try {
      const stored = localStorage.getItem(LAYOUT_STORAGE_KEY)
      if (!stored) return undefined
      const parsed = JSON.parse(stored) as number[]
      if (parsed.length === panelCount) return parsed
      return undefined
    } catch (err) {
      console.warn('[WorkspaceManager] Failed to restore panel layout from localStorage:', err)
      return undefined
    }
  }, [])

  // Local phase state -- derived from server workspace but with local overrides
  // for fast transitions before server round-trips complete
  const [localPhaseOverride, setLocalPhaseOverride] = useState<string | null>(null)
  const [localTypeOverride, setLocalTypeOverride] = useState<WorkspaceType | null>(null)
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false)
  // True while intake file uploads are in flight; gates IntakeProcessor's
  // pipeline auto-start (agents must never run against a partial chart).
  const [intakeUploadsPending, setIntakeUploadsPending] = useState(false)
  const [showManagerDialog, setShowManagerDialog] = useState(false)

  // Ref for tracking background agent completion subscription
  const backgroundSubRef = useRef<(() => void) | null>(null)

  // Track whether background agents are still generating content
  const [isAgentsGenerating, setIsAgentsGenerating] = useState(false)

  // Derive active phase from server workspace state + local override
  // Default to 'workspace' if a workspace is loaded but active_phase is null
  const activePhase = localPhaseOverride
    ?? hydrationData?.workspace?.active_phase
    ?? (hydrationData?.workspace ? 'workspace' : null)


  const activeType: WorkspaceType = localTypeOverride
    ?? hydrationData?.workspace?.type
    ?? 'admission'

  // Push header info up to App.tsx whenever phase/type/display_name changes
  useEffect(() => {
    onHeaderChange?.({
      displayName: hydrationData?.workspace?.display_name ?? null,
      phase: activePhase ?? null,
      type: activeType ?? null,
    })
  }, [activePhase, activeType, hydrationData?.workspace?.display_name, onHeaderChange])

  // Wire notification callback into ChatContext so SSE events reach the App-level bell
  useEffect(() => {
    if (onAddNotification) {
      setNotificationCallback(onAddNotification)
    }
    return () => setNotificationCallback(null)
  }, [onAddNotification, setNotificationCallback])

  // Wire clinical data refresh callback into ChatContext so SSE complete triggers patient panel refresh
  useEffect(() => {
    if (activeWorkspaceId && refreshClinicalData) {
      setRefreshClinicalDataCallback(() => refreshClinicalData(activeWorkspaceId))
    }
    return () => setRefreshClinicalDataCallback(null)
  }, [activeWorkspaceId, refreshClinicalData, setRefreshClinicalDataCallback])

  // Wire artifact refresh + canvas update callbacks into ChatContext so SSE events reach canvas
  useEffect(() => {
    if (!activeWorkspaceId) return

    setRefreshArtifactCallback((artifactId: string) => refreshArtifact(activeWorkspaceId, artifactId))
    setCanvasUpdateCallback((tab: string, content: string) => {
      canvas.setTabContentFromEvent(tab, content)
    })

    return () => {
      setRefreshArtifactCallback(null)
      setCanvasUpdateCallback(null)
    }
  }, [activeWorkspaceId, refreshArtifact, setRefreshArtifactCallback, setCanvasUpdateCallback, canvas.setTabContentFromEvent])

  // Push file info up to App.tsx header whenever hydration data changes
  useEffect(() => {
    if (!onFilesChange) return

    const uploadedFiles = (hydrationData?.files || []).map((f) => ({
      id: f.id,
      name: f.filename || 'Unknown',
      type: f.content_type || f.content_category || 'file',
      timestamp: f.created_at || '',
    }))

    const artifacts = (hydrationData?.artifacts || []).map((a) => ({
      id: a.id,
      title: a.title || 'Untitled',
      type: a.type || 'document',
      timestamp: a.updated_at || a.created_at || '',
      artifactId: a.id,
    }))

    onFilesChange({ uploadedFiles, artifacts, workspaceId: activeWorkspaceId || '' })
  }, [hydrationData, onFilesChange, activeWorkspaceId])

  // Expose canvas.setActiveTab to parent via ref (for artifact link navigation)
  useEffect(() => {
    if (canvasTabRef) {
      canvasTabRef.current = (tab: string) => {
        canvas.setActiveTab(tab as import('../../hooks/useCanvas').CanvasTab)
      }
    }
    return () => { if (canvasTabRef) canvasTabRef.current = null }
  }, [canvasTabRef, canvas.setActiveTab])

  // Register the sidebar toggle so the global header's mobile hamburger can open
  // the drawer. Mirrors canvasTabRef, but via a callback so the button can be
  // omitted until a handler exists.
  useEffect(() => {
    onRegisterMobileMenu?.(toggleSidebar)
    return () => onRegisterMobileMenu?.(null)
  }, [onRegisterMobileMenu, toggleSidebar])

  // Load persisted notifications from hydration data
  useEffect(() => {
    if (!onLoadNotifications || !hydrationData?.notifications || !activeWorkspaceId) return
    onLoadNotifications(hydrationData.notifications, activeWorkspaceId)
  }, [hydrationData?.notifications, activeWorkspaceId, onLoadNotifications])

  // Clear local overrides when workspace switches, unless creation just happened
  const skipNextClearRef = useRef(false)
  useEffect(() => {
    if (skipNextClearRef.current) {
      skipNextClearRef.current = false
      return
    }
    setLocalPhaseOverride(null)
    setLocalTypeOverride(null)
    // Fix #259: Tear down stale background subscription and reset generating state
    // on workspace switch. Without this, the subscription callback fires against
    // the wrong workspace and isAgentsGenerating stays stuck as true.
    backgroundSubRef.current?.()
    backgroundSubRef.current = null
    setIsAgentsGenerating(false)
  }, [activeWorkspaceId])

  // Sync workspace ID to ChatContext so agent chat sends the correct workspace ID
  useEffect(() => {
    setChatWorkspaceId(activeWorkspaceId)
  }, [activeWorkspaceId, setChatWorkspaceId])

  // Load the correct chat session when switching between chat workspaces
  useEffect(() => {
    if (activeType === 'chat' && activeWorkspaceId) {
      const uiConvId = hydrationData?.workspace?.ui_state?.conversation_id
      loadSessionForWorkspace(activeWorkspaceId, typeof uiConvId === 'string' ? uiConvId : undefined)
    }
  }, [activeWorkspaceId, activeType, loadSessionForWorkspace, hydrationData?.workspace?.ui_state?.conversation_id])

  // #444: the manual-entry dialog saves through the leg that owns the panel.
  // Chat workspaces key on the chat conversation (which resolves to the
  // workspace row); admission/flow have no chat conversation, so the save is
  // keyed directly on the workspace the panel hydrates from. Both legs
  // parse + save + notify Casey backend-side.
  const handlePatientDataSave = useCallback(async (text: string) => {
    if (activeType === 'chat') {
      return parseAndSavePatientText(text)
    }
    if (!activeWorkspaceId) {
      // Edit buttons only render inside an active workspace; this guards the
      // impossible-by-construction path rather than saving an orphan. If that
      // assumption ever breaks, the clinician must not be told to "try again" —
      // no number of retries creates an active workspace — and there must be a
      // record that the impossible happened.
      console.error('[WorkspaceManager] patient save with no active workspace', { activeType })
      throw new PatientDataSaveError(
        'No active workspace to save into. Open or create a patient workspace first.'
      )
    }
    return parseAndSavePatientTextForWorkspace(activeWorkspaceId, text)
  }, [activeType, activeWorkspaceId, parseAndSavePatientText, parseAndSavePatientTextForWorkspace])

  // Session-based workspace mapping
  const chatWorkspaces = useMemo(() => sessions.map(session => ({
    id: session.id,
    title: session.title || 'New Chat',
    messageCount: session.message_count || 0,
    createdAt: new Date(session.created_at),
    lastActivity: new Date(session.last_activity),
  })), [sessions])

  const activeWorkspace = useMemo(
    () => chatWorkspaces.find(ws => ws.id === currentSessionId),
    [chatWorkspaces, currentSessionId]
  )

  /**
   * IntakePage submission -- create workspace, upload files, start pipeline.
   * Phase transition: intake -> processing (or intake -> workspace for chat)
   */
  const intakeInFlightRef = useRef(false)
  const handleIntakeSubmit = useCallback(async (
    displayName: string,
    type: WorkspaceType,
    intakeFiles: File[],
  ) => {
    // Guard against double-submission (button disable is async, fast clicks can race)
    if (intakeInFlightRef.current) return
    intakeInFlightRef.current = true
    setIsCreatingWorkspace(true)
    try {
      // ─── overlay-handoff branch ───
      const isOverlayHandoff =
        !!activeWorkspaceId &&
        hydrationData?.workspace?.active_phase === 'intake' &&
        hydrationData?.workspace?.ui_state?.created_via_overlay === true

      if (isOverlayHandoff && activeWorkspaceId) {
        // Upload any locally-dropped files first (existing files are already on the server).
        // Sequential: chart text must assemble in the order the physician gave the files.
        if (intakeFiles.length > 0) {
          const failed = failedFileNames(await uploadFilesInOrder(activeWorkspaceId, intakeFiles))
          if (failed.length > 0) {
            onAddNotification?.('clinical_alert', `Upload failed: ${failed.join(', ')} — intake not started. Please try again.`)
            return
          }
        }

        // Server-authoritative kickoff
        const trimmed = displayName.trim()
        const currentName = hydrationData?.workspace?.display_name ?? ''
        const renameArg = (trimmed.length > 0 && trimmed !== currentName) ? trimmed : undefined
        const result = await workspaceService.startIntake(activeWorkspaceId, { displayName: renameArg })

        if (result.status === 'started' || result.status === 'restarted') {
          startPipeline(activeWorkspaceId, activeWorkspaceId)
          skipNextClearRef.current = true
          setLocalPhaseOverride('processing')
          // overlay-created workspaces are always admission type (overlay-handoff invariant)
          setLocalTypeOverride('admission')
        } else if (result.status === 'already_running') {
          // Other tab beat us to it — surface processing UI without re-firing the pipeline.
          skipNextClearRef.current = true
          setLocalPhaseOverride('processing')
          setLocalTypeOverride('admission')
        } else if (result.status === 'already_complete') {
          skipNextClearRef.current = true
          setLocalPhaseOverride('workspace')
          setLocalTypeOverride('admission')
          saveState({ active_phase: 'workspace' })
          rehydrate(activeWorkspaceId).catch(err =>
            console.error('[WorkspaceManager] Rehydrate after already_complete failed:', err)
          )
        } else {
          // not_eligible: usually a server/client state divergence (e.g., another
          // tab closed the workspace, or the overlay marker was cleared). Re-sync
          // hydration so the UI reflects what the server actually says, instead
          // of leaving the user stuck on IntakePage with a dead Start button.
          console.error('[WorkspaceManager] Overlay-handoff start refused:', result.reason)
          onAddNotification?.('clinical_alert', `Could not start intake: ${result.reason ?? 'unknown'}`)
          rehydrate(activeWorkspaceId).catch(err =>
            console.error('[WorkspaceManager] Rehydrate after not_eligible failed:', err)
          )
        }
        return
      }
      // ─── END overlay-handoff branch — existing manual flow below unchanged ───

      const name = displayName || undefined
      skipNextClearRef.current = true
      const workspaceId = await createWorkspace(type, name)

      // Chat type skips processing -- straight to workspace
      if (type === 'chat') {
        // Upload files in background if any (no processing spinner).
        // Sequential: chart text must assemble in the order the physician gave the files.
        if (intakeFiles.length > 0) {
          uploadFilesInOrder(workspaceId, intakeFiles).then(results => {
            const failed = failedFileNames(results)
            if (failed.length > 0) {
              console.warn(`[Intake] Failed uploads: ${failed.join(', ')}`)
              // TODO: Surface as user-visible toast/notification once chat loads.
              // addMessage from ChatContext not available here during phase transition.
            }
          })
        }
        setLocalPhaseOverride('workspace')
        setLocalTypeOverride('chat')
        saveState({ active_phase: 'workspace' })
        return
      }

      // Flip to 'processing' immediately for feedback, but hold the pipeline
      // until every upload lands: uploadsPending suppresses IntakeProcessor's
      // mount auto-start, so a still-uploading document can't be missing from
      // the chart the agents read.
      setIntakeUploadsPending(intakeFiles.length > 0)
      setLocalPhaseOverride('processing')
      setLocalTypeOverride(type)

      if (intakeFiles.length > 0) {
        let failed: string[]
        try {
          failed = failedFileNames(await uploadFilesInOrder(workspaceId, intakeFiles))
        } finally {
          setIntakeUploadsPending(false)
        }
        if (failed.length > 0) {
          onAddNotification?.('clinical_alert', `Upload failed: ${failed.join(', ')} — intake not started. Please try again.`)
          skipNextClearRef.current = false
          setLocalPhaseOverride(null)
          setLocalTypeOverride(null)
          return
        }
      }

      // Start the appropriate pipeline — chart data reaches agents server-side
      if (type === 'flow') {
        startFlowPipeline(workspaceId, workspaceId)
      } else {
        startPipeline(workspaceId, workspaceId)
      }
    } catch (err) {
      // Covers thrown failures from both paths: the overlay-handoff
      // startIntake/upload calls and the manual createWorkspace flow. Without a
      // user-visible signal, a 500/404/network drop just silently resets the UI
      // back to IntakePage, leaving the Start button looking idle (dead button).
      console.error('[WorkspaceManager] Failed to start intake:', err)
      onAddNotification?.('clinical_alert', 'Something went wrong starting intake. Please try again.')
      skipNextClearRef.current = false
      setLocalPhaseOverride(null)
      setLocalTypeOverride(null)
    } finally {
      setIsCreatingWorkspace(false)
      intakeInFlightRef.current = false
    }
  }, [createWorkspace, saveState, activeWorkspaceId, hydrationData, rehydrate, onAddNotification])

  /**
   * IntakeProcessor completion -- transition from processing to workspace.
   * Subscribes to background agents if any are still running.
   */
  const handleIntakeComplete = useCallback((_results: Record<string, unknown>) => {
    setLocalPhaseOverride('workspace')
    saveState({ active_phase: 'workspace' })

    if (activeWorkspaceId) {
      // Re-hydrate to pick up artifacts and conversations created by the pipeline
      rehydrate(activeWorkspaceId).catch(err =>
        console.error('[WorkspaceManager] Post-pipeline rehydration failed:', err)
      )

      const snap = getPipelineSnapshot(activeWorkspaceId)
      if (snap) {
        const allDone = PIPELINE_AGENT_IDS.every(id =>
          snap.agentStatus[id] === 'complete' || snap.agentStatus[id] === 'error'
        )
        if (allDone) {
          clearPipeline(activeWorkspaceId)
        } else {
          setIsAgentsGenerating(true)
          // Track last-seen status per agent to detect transitions that warrant re-hydration
          const lastSeenStatus = new Map<string, string>()
          for (const id of PIPELINE_AGENT_IDS) {
            lastSeenStatus.set(id, snap.agentStatus[id] || 'waiting')
          }

          backgroundSubRef.current?.()
          backgroundSubRef.current = subscribePipeline(activeWorkspaceId, (newSnap) => {
            const nowDone = PIPELINE_AGENT_IDS.every(id =>
              newSnap.agentStatus[id] === 'complete' || newSnap.agentStatus[id] === 'error'
            )

            // Progressive hydration: re-hydrate on any status transition that produces data
            let needsHydration = false
            for (const id of PIPELINE_AGENT_IDS) {
              const prev = lastSeenStatus.get(id)
              const curr = newSnap.agentStatus[id]
              if (curr !== prev && (curr === 'data-ready' || curr === 'complete' || curr === 'error')) {
                needsHydration = true
              }
              lastSeenStatus.set(id, curr)
            }

            if (needsHydration && activeWorkspaceId) {
              rehydrate(activeWorkspaceId).catch((err) => { console.error('[WorkspaceManager] Progressive rehydration failed:', err) })
            }

            if (nowDone) {
              setIsAgentsGenerating(false)
              clearPipeline(activeWorkspaceId)
              backgroundSubRef.current?.()
              backgroundSubRef.current = null
            }
          })
        }
      }
    }
  }, [activeWorkspaceId, saveState, rehydrate])

  /**
   * FlowProcessor completion -- transition from processing to workspace.
   */
  const handleFlowComplete = useCallback((_results: FlowWorkspaceResults) => {
    setLocalPhaseOverride('workspace')
    saveState({ active_phase: 'workspace' })

    if (activeWorkspaceId) {
      clearFlowPipeline(activeWorkspaceId)
    }
  }, [activeWorkspaceId, saveState])

  /**
   * Flow -> Admission conversion.
   * Phase transition: workspace (flow) -> processing (admission)
   */
  const handleConvertToAdmission = useCallback(() => {
    if (!activeWorkspaceId) return

    setLocalTypeOverride('admission')
    setLocalPhaseOverride('processing')

    // Persist marker so flow-chat tab remains visible + read-only after refresh
    // (CasePanelChat reads ui_state.created_from_flow).
    saveState({ ui_state: { created_from_flow: true } })

    // Start admission pipeline (picks up IPASS context from sessionStorage)
    startPipeline(activeWorkspaceId, activeWorkspaceId)
  }, [activeWorkspaceId, saveState])

  // Hydrate canvas + agent sessions + patient panel from backend when entering workspace phase.
  // Split into per-slice keys so granular refreshes (e.g. clinical data only) don't
  // reset unrelated state (e.g. agent sessions / canvas tabs).
  const lastArtifactsKeyRef = useRef<string | null>(null)
  const lastConversationsKeyRef = useRef<string | null>(null)
  const lastClinicalKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (activePhase !== 'workspace') {
      lastArtifactsKeyRef.current = null
      lastConversationsKeyRef.current = null
      lastClinicalKeyRef.current = null
      setPatientDataDirect(null)
      clearCanvases()
      clearAgentSessions()
      return
    }
    if (!hydrationData) return

    // --- Artifacts → canvas tabs ---
    const latestArtifactUpdate = hydrationData.artifacts?.reduce((max, a) => a.updated_at > max ? a.updated_at : max, '') || ''
    const artifactsKey = `${hydrationData.workspace?.id}-${hydrationData.artifacts?.length}-${latestArtifactUpdate}`
    if (lastArtifactsKeyRef.current !== artifactsKey) {
      lastArtifactsKeyRef.current = artifactsKey
      if (hydrationData.artifacts?.length) {
        const mapped = hydrationData.artifacts.map(a => ({
          id: a.id,
          type: a.type,
          title: a.title,
          content: a.current_content,
          current_content: a.current_content,
        }))
        loadFromArtifacts(mapped)
      } else {
        clearCanvases()
      }
    }

    // --- Conversations → agent sessions ---
    const conversationsKey = `${hydrationData.workspace?.id}-${hydrationData.conversations?.length}`
    if (lastConversationsKeyRef.current !== conversationsKey) {
      lastConversationsKeyRef.current = conversationsKey
      if (hydrationData.conversations?.length) {
        loadAgentSessions(hydrationData.conversations)
      } else {
        clearAgentSessions()
      }
    }

    // --- Clinical data → patient panel ---
    const clinicalKey = `${hydrationData.workspace?.id}-${hydrationData.clinical_data?.id || 'none'}-${hydrationData.clinical_data?.updated_at || ''}`
    if (lastClinicalKeyRef.current !== clinicalKey) {
      lastClinicalKeyRef.current = clinicalKey
      if (hydrationData.clinical_data) {
        const cd = hydrationData.clinical_data
        setPatientDataDirect({
          id: cd.id,
          patient_summary: cd.patient_summary || '',
          allergies: Array.isArray(cd.allergies) ? cd.allergies : [],
          code_status: null,
          source_description: '',
          extractions: (cd.extractions || []) as import('../../contexts/ChatContext').SemanticExtraction[],
          panel_content: cd.panel_content || null,
          extraction_model: 'gpt-5.2',
          created_at: '',
          updated_at: '',
        })
      } else {
        setPatientDataDirect(null)
      }
    }
  }, [activePhase, hydrationData, loadFromArtifacts, loadAgentSessions, setPatientDataDirect, clearCanvases, clearAgentSessions])

  // Cleanup background subscription on unmount
  useEffect(() => {
    return () => {
      backgroundSubRef.current?.()
    }
  }, [])

  // Auto-name workspace when hydration data arrives with patient summary.
  // Only fires in workspace phase, only when the name is still the default "New Patient".
  useEffect(() => {
    if (activePhase !== 'workspace') return
    if (!activeWorkspaceId || !hydrationData?.workspace) return

    // Only auto-name if still showing "New Patient" default name
    const currentName = hydrationData.workspace.display_name
    if (currentName && currentName !== 'New Patient') return

    // Try to extract a oneliner from patient summary
    const summary = hydrationData.clinical_data?.patient_summary
    if (!summary) return

    // Extract first sentence or first ~60 chars
    const firstSentence = summary.split(/[.\n]/)[0]?.trim()
    if (!firstSentence) return

    const oneliner = firstSentence.length > 60
      ? firstSentence.slice(0, 57) + '...'
      : firstSentence

    saveState({ display_name: oneliner })
  }, [activePhase, activeWorkspaceId, hydrationData?.workspace?.display_name, hydrationData?.clinical_data?.patient_summary, saveState])

  // Auto-create fresh chat on mount for immediate UX
  useEffect(() => {
    createNewChat()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Suppress lint warnings for variables referenced only in JSX
  void chatWorkspaces

  const openManagerDialog = useCallback(() => {
    setShowManagerDialog(true)
  }, [])

  const sidebarContent = (
    <Sidebar
      isOpen={showSidebar}
      onToggle={toggleSidebar}
      userName={userFullName}
      userInitial={userFirstName?.[0]?.toUpperCase()}
      onOpenSettings={onOpenSettings}
      onOpenManager={openManagerDialog}
      onNavigate={closeMobileSidebar}
    />
  )

  /**
   * Phase-based content renderer.
   *
   * - null / 'intake': IntakePage
   * - 'processing': IntakeProcessor or FlowProcessor
   * - 'workspace': Main workspace (ChatUI + canvas + patient panel)
   */
  const renderPhaseContent = () => {
    switch (activePhase) {
      case 'processing':
        if (activeType === 'flow') {
          return (
            <FlowProcessor
              workspaceId={activeWorkspaceId}
              onComplete={handleFlowComplete}
              onConvertToAdmission={handleConvertToAdmission}
            />
          )
        }
        return (
          <IntakeProcessor
            workspaceId={activeWorkspaceId || ''}
            onComplete={handleIntakeComplete}
            uploadsPending={intakeUploadsPending}
          />
        )

      case 'workspace':
        // Route by workspace type: admission/flow get CasePanelChat, chat gets ChatUI
        if (activeType === 'admission' || activeType === 'flow') {
          // Always show patient panel for admission/flow (CasePanelChat has no header mode)
          const showPatientPanel = !isMobile

          // Mobile: one pane at a time (Patient · Chat · Note) via a bottom switcher.
          if (isMobile) {
            return (
              <MobileWorkspaceShell
                ref={mobileShellRef}
                patientPane={
                  patientData ? (
                    <div className="h-full w-full overflow-y-auto">
                      <PatientPanel
                        data={fromExtractions(patientData, undefined, isAgentsGenerating, patientData.panel_content, {
                          vitals: hydrationData?.clinical_data?.vitals ?? null,
                          labs: hydrationData?.clinical_data?.labs ?? null,
                          imaging: hydrationData?.clinical_data?.imaging ?? null,
                          orders: hydrationData?.clinical_data?.orders ?? null,
                          medications: hydrationData?.clinical_data?.medications ?? null,
                          summary_presentation: hydrationData?.clinical_data?.summary_presentation ?? null,
                          summary_admission_context: hydrationData?.clinical_data?.summary_admission_context ?? null,
                        })}
                        onCollapseToHeader={() => mobileShellRef.current?.setActivePane('chat')}
                        onEdit={() => setShowPatientDataInput(true)}
                        onClear={clearPatientData}
                        onReconcile={reconcilePatientData}
                        isReconciling={isReconciling}
                        showToolbar
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col h-full items-center justify-center text-muted-foreground text-sm gap-2 p-4 text-center">
                      <Upload className="h-6 w-6 text-muted-foreground/40" />
                      <span>No patient data yet</span>
                      <span className="text-xs text-muted-foreground/60">Upload chart files to populate the patient panel</span>
                    </div>
                  )
                }
                chatPane={
                  // No onExpandToPanel on mobile: ThinkingSidePanel (the consumer
                  // of expandedThinking) is only rendered in the desktop branch, so
                  // wiring the expand button here would make it a silent no-op.
                  // Omitting the prop hides the button on mobile (ThinkingPanel
                  // gates it on the callback's presence).
                  <CasePanelChat
                    workspaceId={activeWorkspaceId ?? undefined}
                    onOpenCanvas={(tab) => {
                      canvas.setActiveTab(tab as import('../../hooks/useCanvas').CanvasTab)
                      mobileShellRef.current?.setActivePane('note')
                    }}
                  />
                }
                notePane={
                  <CanvasPanel canvas={canvas} isGenerating={isAgentsGenerating} lastTurnMeta={lastTurnMeta} />
                }
              />
            )
          }

          // Desktop: resizable panel layout (stable 3-panel key to avoid re-mount flicker)
          const savedLayout = getSavedLayout(showPatientPanel ? 3 : 2)
          return (
            <div className="flex h-full overflow-hidden">
            <ResizablePanelGroup
              key={showPatientPanel ? 'panels-3' : 'panels-2'}
              direction="horizontal"
              onLayout={handlePanelLayout}
              className="h-full flex-1"
            >
              {showPatientPanel && (
                <>
                  <ResizablePanel
                    ref={patientPanelRef as RefObject<ImperativePanelHandle>}
                    defaultSize={savedLayout?.[0] ?? 20}
                    minSize={8}
                    maxSize={35}
                    collapsible
                    collapsedSize={3}
                    onCollapse={() => setIsPatientPanelCollapsed(true)}
                    onExpand={() => setIsPatientPanelCollapsed(false)}
                    order={1}
                  >
                    {isPatientPanelCollapsed ? (
                      <button
                        onClick={() => patientPanelRef.current?.expand()}
                        className="flex flex-col items-center justify-center h-full w-full gap-3 hover:bg-clinical-cyan/5 transition-colors cursor-pointer"
                        title="Expand patient panel"
                      >
                        <div className="w-8 h-8 rounded-lg bg-clinical-cyan/15 border border-clinical-cyan/40 flex items-center justify-center shadow-[0_0_10px_rgba(0,200,200,0.15)]">
                          <Stethoscope className="h-4 w-4 text-clinical-cyan" />
                        </div>
                        <span className="text-[10px] text-clinical-cyan/70 font-medium tracking-wider [writing-mode:vertical-lr]">PATIENT</span>
                      </button>
                    ) : patientData ? (
                      <PatientPanel
                        data={fromExtractions(patientData, undefined, isAgentsGenerating, patientData.panel_content, {
                          vitals: hydrationData?.clinical_data?.vitals ?? null,
                          labs: hydrationData?.clinical_data?.labs ?? null,
                          imaging: hydrationData?.clinical_data?.imaging ?? null,
                          orders: hydrationData?.clinical_data?.orders ?? null,
                          medications: hydrationData?.clinical_data?.medications ?? null,
                          summary_presentation: hydrationData?.clinical_data?.summary_presentation ?? null,
                          summary_admission_context: hydrationData?.clinical_data?.summary_admission_context ?? null,
                        })}
                        onCollapseToHeader={() => patientPanelRef.current?.collapse()}
                        onEdit={() => setShowPatientDataInput(true)}
                        onClear={clearPatientData}
                        onReconcile={reconcilePatientData}
                        isReconciling={isReconciling}
                        showToolbar
                        extractionProgress={
                          deepExtractingFile
                            ? { isActive: true, message: getExtractionMessage() }
                            : undefined
                        }
                      />
                    ) : (
                      <div className="flex flex-col h-full items-center justify-center text-muted-foreground text-sm gap-2 p-4 text-center">
                        <Upload className="h-6 w-6 text-muted-foreground/40" />
                        <span>No patient data yet</span>
                        <span className="text-xs text-muted-foreground/60">Upload chart files to populate the patient panel</span>
                      </div>
                    )}
                  </ResizablePanel>
                  <ResizableHandle
                    withHandle
                    onDoubleClick={() => {
                      if (isPatientPanelCollapsed) {
                        patientPanelRef.current?.expand()
                      } else {
                        patientPanelRef.current?.collapse()
                      }
                    }}
                  />
                </>
              )}

              <ResizablePanel
                defaultSize={savedLayout?.[showPatientPanel ? 1 : 0] ?? (showPatientPanel ? 40 : 50)}
                minSize={25}
                order={2}
              >
                <CasePanelChat workspaceId={activeWorkspaceId ?? undefined} onOpenCanvas={handleOpenCanvas} onExpandToPanel={handleExpandThinking} />
              </ResizablePanel>

              <ResizableHandle
                withHandle
                onDoubleClick={() => {
                  if (isCanvasPanelCollapsed) {
                    canvasPanelRef.current?.expand()
                  } else {
                    canvasPanelRef.current?.collapse()
                  }
                }}
              />

              <ResizablePanel
                ref={canvasPanelRef as RefObject<ImperativePanelHandle>}
                defaultSize={savedLayout?.[showPatientPanel ? 2 : 1] ?? (showPatientPanel ? 40 : 50)}
                minSize={8}
                collapsible
                collapsedSize={3}
                onCollapse={() => setIsCanvasPanelCollapsed(true)}
                onExpand={() => setIsCanvasPanelCollapsed(false)}
                order={3}
              >
                {isCanvasPanelCollapsed ? (
                  <button
                    onClick={() => canvasPanelRef.current?.expand()}
                    className="flex flex-col items-center justify-center h-full w-full gap-3 hover:bg-purple-500/5 transition-colors cursor-pointer border-l border-border"
                    title="Expand clinical note"
                  >
                    <div className="w-8 h-8 rounded-lg bg-purple-500/15 border border-purple-500/40 flex items-center justify-center shadow-[0_0_10px_rgba(168,85,247,0.15)]">
                      <FileText className="h-4 w-4 text-purple-400" />
                    </div>
                    <span className="text-[10px] text-purple-400/70 font-medium tracking-wider [writing-mode:vertical-lr]">NOTE</span>
                  </button>
                ) : (
                  <CanvasPanel canvas={canvas} isGenerating={isAgentsGenerating} onCollapse={() => canvasPanelRef.current?.collapse()} lastTurnMeta={lastTurnMeta} />
                )}
              </ResizablePanel>
            </ResizablePanelGroup>
            {expandedThinking && (
              <ThinkingSidePanel
                content={expandedThinking.content}
                durationMs={expandedThinking.durationMs}
                isStreaming={expandedThinking.isStreaming}
                onClose={handleCloseThinking}
              />
            )}
            </div>
          )
        }

        return (
          <>
            {patientData && !isMobile && patientPanelMode === 'column' && (
              <div className="h-full flex-shrink-0 w-[280px] border-r border-border/40">
                <PatientPanel
                  data={fromExtractions(patientData, undefined, isAgentsGenerating, patientData.panel_content, {
                    vitals: hydrationData?.clinical_data?.vitals ?? null,
                    labs: hydrationData?.clinical_data?.labs ?? null,
                    imaging: hydrationData?.clinical_data?.imaging ?? null,
                    orders: hydrationData?.clinical_data?.orders ?? null,
                    medications: hydrationData?.clinical_data?.medications ?? null,
                    summary_presentation: hydrationData?.clinical_data?.summary_presentation ?? null,
                    summary_admission_context: hydrationData?.clinical_data?.summary_admission_context ?? null,
                  })}
                  onCollapseToHeader={togglePatientPanelMode}
                  onEdit={() => setShowPatientDataInput(true)}
                  onClear={clearPatientData}
                  onReconcile={reconcilePatientData}
                  isReconciling={isReconciling}
                  showToolbar
                />
              </div>
            )}

            <div className="flex flex-col h-full flex-1 min-w-0 bg-background relative">
              <div className="flex-1 min-h-0 overflow-hidden">
                {viewMode === 'project-detail' && activeProjectId ? (
                  <ProjectDetailView
                    projectId={activeProjectId}
                    onClose={closeProject}
                  />
                ) : (
                  <ChatUI
                    patientId={activeWorkspace?.id || 'default'}
                    patientName={activeWorkspace?.title || 'New Chat'}
                    userFirstName={userFirstName}
                  />
                )}
              </div>
            </div>

          </>
        )

      default:
        if (isHydrating) {
          return (
            <div className="flex flex-col h-full flex-1 min-w-0 bg-background relative">
              <WorkspaceLoadingSkeleton />
            </div>
          )
        }
        return (
          <div className="flex flex-col h-full flex-1 min-w-0 bg-background relative">
            <IntakePage
              onWorkspaceCreated={handleIntakeSubmit}
              isCreating={isCreatingWorkspace}
              initialType={pendingWorkspaceType ?? undefined}
              existingFiles={hydrationData?.files}
              existingDisplayName={hydrationData?.workspace?.display_name}
            />
          </div>
        )
    }
  }

  return (
    <div className="flex h-full w-full max-w-full overflow-hidden relative">
      {/* Mobile: overlay drawer */}
      {isMobile && (
        <MobileSidebarDrawer isOpen={showSidebar} onClose={toggleSidebar}>
          {sidebarContent}
        </MobileSidebarDrawer>
      )}

      {/* Desktop: inline sidebar */}
      {!isMobile && showSidebar && (
        <div className="relative h-full flex-shrink-0 w-[280px] border-r border-border/50 bg-surface overflow-hidden">
          {sidebarContent}
        </div>
      )}

      {!showSidebar && !isMobile && (
        <SidebarRail
          onExpand={toggleSidebar}
          userInitial={userFirstName?.[0]?.toUpperCase()}
        />
      )}

      {/* Main content area — click to dismiss sidebar on desktop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden" onClick={dismissSidebar}>
        {renderPhaseContent()}
      </div>

      {/* Workspace Manager dialog */}
      <WorkspaceManagerDialog
        open={showManagerDialog}
        onOpenChange={setShowManagerDialog}
      />

      {/* Manual patient-data entry (#444) — rendered once here because every
          workspace type's Edit entry point (the PatientPanel toolbars and
          ChatUI's PatientContextHeader) lives inside this subtree. The save
          handler routes by workspace type: chat keys on the conversation,
          admission/flow key directly on the workspace the panel hydrates
          from. Upload remains the primary intake path. */}
      <PatientDataInputDialog
        open={showPatientDataInput}
        onOpenChange={setShowPatientDataInput}
        onSave={handlePatientDataSave}
      />

      {/* Encounter overlays — rendered globally so they persist across view changes */}
      <ConsentModal
        open={encounterStatus === 'consent'}
        onClose={resetEncounter}
      />

      {(encounterStatus === 'recording' || encounterStatus === 'paused') && (
        <EncounterRecorder />
      )}

      {(encounterStatus === 'processing' || encounterStatus === 'generating') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-lg font-medium">
              {encounterStatus === 'processing' ? 'Processing transcript...' : 'Generating clinical note...'}
            </p>
          </div>
        </div>
      )}

      {encounterStatus === 'error' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center max-w-md">
            <p className="text-red-500 text-lg font-medium mb-2">Encounter Error</p>
            <p className="text-sm text-muted-foreground mb-4">{encounterError || 'An unknown error occurred'}</p>
            <button onClick={resetEncounter} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {encounterStatus === 'complete' && generatedNoteContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Generated Note</h2>
              <button onClick={resetEncounter} className="text-gray-500 hover:text-gray-700">
                Close
              </button>
            </div>
            {encounterWarnings.length > 0 && (
              <div className="mb-3 space-y-2">
                {encounterWarnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-200">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}
            <NoteWithSources
              noteContent={generatedNoteContent}
              transcript={transcript}
              groundings={[]}
            />
          </div>
        </div>
      )}
    </div>
  )
}
